/**
 * connectorRefs — canonical helper for the two ways a Canvas node may
 * reference an external connector.
 *
 * Background
 * ----------
 * Two shapes are in the wild:
 *
 *   1. **`config.connectorId`** — the backend's connector UUID (the row id
 *      of an *installed* connector for the active tenant). Written by the
 *      Canvas Config picker and by code paths that explicitly choose a
 *      connector instance.
 *
 *   2. **`config.connector`** — a connector *slug / marketplace id* like
 *      `"email-security"` or `"virustotal"`. Written by playbook templates
 *      (see `data/soarPlaybooks.js`) and by older saved workflows from
 *      before the picker enforced the UUID shape.
 *
 * The Canvas Config tab historically only read `connectorId`. That meant
 * any node carrying just `connector` ("email-security") fell through to a
 * "No configuration available" placeholder — even though the underlying
 * connector might be installed and ready to go. See P0-B in
 * CANVAS_PRODUCTION_AUDIT.md.
 *
 * This module is the single source of truth for normalising between the
 * two shapes. Always go through these helpers — do not write ad-hoc lookup
 * logic at call sites.
 *
 * Security note
 * -------------
 * Lookups never leave the browser. We match against `installedConnectors`
 * (the tenant's own backend rows) and `marketplaceConnectors` (static
 * registry shipped in the build). No connector secrets, URLs, or auth
 * material is ever included in the result.
 */

/**
 * Lowercase + trim a string-ish field, returning null if it isn't a usable
 * string. Used as the comparison key everywhere below so callers don't have
 * to remember to normalise their inputs.
 *
 * @param {*} v
 * @returns {string|null}
 */
function norm(v) {
  if (typeof v !== 'string') return null;
  const trimmed = v.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Given a connector slug (e.g. `"email-security"`) and the tenant's
 * installed connectors, return the matching installed connector — or null.
 *
 * Match cascade, in priority order:
 *   1. installed.marketplaceId === slug
 *   2. installed.type === slug
 *   3. installed.connectorType === slug
 *   4. installed.id === slug (rare; defensive in case caller already had a UUID)
 *   5. installed.name lowercased equals slug lowercased
 *
 * Returns the FIRST match found (deterministic by iteration order).
 *
 * @param {string|null|undefined} slug
 * @param {Object<string, object>|object[]|null|undefined} installedConnectors
 *        Either a `{ [id]: connector }` map or an array of connector objects.
 * @returns {object|null}
 */
export function findInstalledBySlug(slug, installedConnectors) {
  const needle = norm(slug);
  if (!needle) return null;
  if (!installedConnectors) return null;

  const list = Array.isArray(installedConnectors)
    ? installedConnectors
    : Object.values(installedConnectors);

  for (const c of list) {
    if (!c || typeof c !== 'object') continue;
    if (norm(c.marketplaceId) === needle) return c;
    if (norm(c.type) === needle) return c;
    if (norm(c.connectorType) === needle) return c;
    if (norm(c.id) === needle) return c;
    if (norm(c.name) === needle) return c;
  }
  return null;
}

/**
 * Given an installed connector and the marketplace registry, return the
 * marketplace definition that backs it (the source of skill metadata).
 * Returns null if the connector is bespoke or the marketplace entry has
 * been removed from the registry.
 *
 * @param {object} installedConnector
 * @param {object[]|null|undefined} marketplaceConnectors
 * @returns {object|null}
 */
export function findMarketplaceForInstalled(installedConnector, marketplaceConnectors) {
  if (!installedConnector || !Array.isArray(marketplaceConnectors)) return null;
  const c = installedConnector;
  for (const mc of marketplaceConnectors) {
    if (!mc) continue;
    if (mc.id && (mc.id === c.type || mc.id === c.connectorType || mc.id === c.marketplaceId)) {
      return mc;
    }
    if (norm(mc.name) && norm(mc.name) === norm(c.name)) return mc;
  }
  return null;
}

/**
 * Normalise a single node's `config.connector` (slug) to `config.connectorId`
 * (UUID), if a matching installed connector exists. Returns a NEW node object
 * — never mutates input.
 *
 * Both fields are kept in the resulting config. `connectorId` is the canonical
 * lookup key going forward; `connector` (slug) is preserved as a hint for
 * future tenants who may not yet have the UUID installed (so re-import in a
 * different tenant still resolves cleanly).
 *
 * If no matching installed connector exists, the node is returned unchanged.
 * The Canvas surfaces this case via `unresolvedConnectorRefs` so the user
 * gets a "Install <X>" banner instead of a silently-broken node.
 *
 * @param {object} node
 * @param {Object<string, object>|object[]} installedConnectors
 * @returns {{ node: object, resolved: boolean, slug: string|null }}
 */
export function normalizeNodeConnectorRef(node, installedConnectors) {
  if (!node || !node.config) return { node, resolved: false, slug: null };
  const cfg = node.config;
  const slug = norm(cfg.connector);
  // Already has connectorId — nothing to do (and a UUID always wins over slug)
  if (cfg.connectorId) return { node, resolved: true, slug };
  if (!slug) return { node, resolved: false, slug: null };

  const installed = findInstalledBySlug(slug, installedConnectors);
  if (!installed || !installed.id) {
    // Slug present but no install — caller surfaces this to the user.
    return { node, resolved: false, slug };
  }

  return {
    node: {
      ...node,
      config: {
        ...cfg,
        connectorId: installed.id,
        // keep the original slug for cross-tenant portability
        connector: cfg.connector,
      },
    },
    resolved: true,
    slug,
  };
}

/**
 * Walk every node in a workflow definition and resolve connector slugs to
 * UUIDs. Returns the new nodes plus a list of slugs we couldn't resolve so
 * the caller can show an "Install these connectors" banner.
 *
 * Safe to call with an empty / undefined workflow — returns an empty result.
 *
 * @param {{nodes: object[], connections?: object[]}} workflow
 * @param {Object<string, object>|object[]} installedConnectors
 * @returns {{
 *   nodes: object[],
 *   connections: object[],
 *   unresolvedSlugs: string[],   // unique, lowercase, trimmed
 *   resolvedCount: number,
 * }}
 */
export function resolveWorkflowConnectorRefs(workflow, installedConnectors) {
  const inNodes = (workflow && workflow.nodes) || [];
  const connections = (workflow && workflow.connections) || [];
  const unresolved = new Set();
  let resolvedCount = 0;

  const outNodes = inNodes.map((n) => {
    const result = normalizeNodeConnectorRef(n, installedConnectors);
    if (!result.resolved && result.slug) {
      unresolved.add(result.slug);
    } else if (result.resolved && result.node !== n) {
      resolvedCount += 1;
    }
    return result.node;
  });

  return {
    nodes: outNodes,
    connections,
    unresolvedSlugs: Array.from(unresolved),
    resolvedCount,
  };
}
